/* eslint-disable jsx-a11y/anchor-is-valid  */

import React from 'react'
import lodash from 'lodash'
import {
  ButtonConfig,
  IconicColor,
  InterfaceProduct,
  InterfaceProductCategory,
  InterfaceProductSubcategory,
  MechProduct,
  SkinProductOptionWithGrid,
} from '../types/productTypes'
import ConfigurableProductView, { ConfigurableProductButtonFrame } from '../components/ConfigurableProductView'
import './ButtonConfigurationScreen.scss'
import * as ButtonOptionIndexDragDrop from './ButtonOptionIndexDragDrop'
import SquareContainer from '../components/SquareContainer'

import addZoomedDragElement from '../utils/addZoomedDragElement'
import ConfigurableProductButton from '../components/ConfigurableProductButton'
import TabSelector, { TabSelectorOption } from '../components/TabSelector'

import classNames from 'classnames'
import Button from '../components/Button'
import ToggleSwitch from '../components/ToggleSwitch'
import ReactResizeDetector from 'react-resize-detector'
import { getSkinErrorImage, getSkinPlaceholderImage } from '../data/test-data'
import {
  findMechByItemNo,
  findParentInterface,
  getAllowedInterfaceCategories,
  getCompatibleInterfaces,
  getDefaultButtons,
  getDefaultSkinOrientation,
  getInterfaceDisplayName,
  getSkinSlotCount,
  inflateConfiguredInterface,
  isLedSupported,
  resolveInterfaces,
} from '../products/productUtils'
import { ProductDataContext } from '../ProductDataContext'
import { validateMechs, ValidationMessage } from '../products/productValidation'
import ConfiguratorAppContext from '../ConfiguratorAppContext'
import { IconPlusWhite, IconReset, IconTick, IconUndo } from '../components/icons'
import WallColors from '../data/WallColors'
import { PlaceholderInterface } from '../data/placeholderImages'
import { CheckBox } from '../components/CheckBox'
import { AppStateActions, WiserMode, WiserOrMomentary } from '../state/AppState'
import windowSupportsTouchEvents from '../utils/windowSupportsTouchEvents'
import { ConfiguredInterface, InflatedConfiguredInterfaceProduct } from '../list/ListTypes'
import Popup from '../components/Popup'
import ValidationBanner from '../components/ValidationBanner'
import { ProductDataShape } from '../ProductDataShape'
import { BuildConfig } from '../config/BuildConfig'
import { ButtonConfigurationGroup } from './ButtonConfigurationGroup'
import { UserType } from '../types/userTypes'
import { ENABLE_ELECTRICIAN_VIEW } from '../environment'
import { WiserHubPromptPopup } from '../wiser/WiserHubPromptPopup'
import productRequiresWiserHub from '../products/productRequiresWiserHub'
import { AnalyticsContext } from '../analytics/AnalyticsContext'
import { CookieSettingsLink } from '../components/CookieSettingsLink'
import { PrivacyPolicyLink } from '../components/PrivacyPolicyLink'

export interface RemoteButtonControls {
  selectedButtonIndex: number
  onSelectButtonOption: (buttonIndex: number, buttonOption: InterfaceProduct | null, mech: MechProduct | null) => boolean
  configuredButtonIds: readonly Readonly<ConfiguredInterface>[]
  shouldShowPackQuantity: boolean
}

const TabOptions: readonly TabSelectorOption<InterfaceProductCategory>[] = [
  {
    key: 'Switches',
    label: 'Pictograms',
  },
  {
    key: 'Pro Placeholder',
    label: 'pro placeholder',
  },
  {
    key: 'Dimmers',
    label: 'Dimmers',
  },
  {
    key: 'Bluetooth',
    label: 'Connected',
  },
  {
    key: 'USB',
    label: 'USB',
  },
  {
    key: 'Network',
    label: 'Network',
  },
  {
    key: 'LED',
    label: 'LED',
  },
  {
    key: 'Rotary Switches',
    label: 'Rotary Switches',
  },
  {
    key: 'Accessories',
    label: 'Accessories',
  },
]

type ButtonIdStack = ReadonlyArray< ReadonlyArray< Readonly <ConfiguredInterface> > >

const CancelPopup: React.FC<{
  onDismiss: () => void
  onConfirm: () => void
}> = ({ onDismiss, onConfirm }) => {
  return (
    <Popup onClose={onDismiss}>
      <div className="title">
        Go Back
      </div>
      <div className="buttonStack">
        <Button buttonStyle="redStroke" onClick={onConfirm}>
          Discard Changes
        </Button>
        <Button buttonStyle="green" onClick={onDismiss}>
          Keep Customising
        </Button>
      </div>
    </Popup>
  )
}

const buttonOptionIndexDragOverHandler = (e: React.DragEvent<HTMLDivElement>) => {
  if (ButtonOptionIndexDragDrop.hasData(e)) {
    e.preventDefault()
  }
}

const DebugValidationMessages: React.FC<{
  validationMessages: readonly ValidationMessage[]
  onClickButtonIndex: (index: number) => void
}> = ({
  validationMessages,
  onClickButtonIndex,
}) => (
  <div className="validationMessages">
    <h3>Validation</h3>
    {
      validationMessages.length > 0 ? (
        <ul>
          {
            validationMessages.map((it, i) => {
              const { itemNo, interfaceIndex } = it
              return (
                <li key={i}>
                  {
                    itemNo && typeof interfaceIndex === 'number' && (
                      <a href="#" onClick={() => onClickButtonIndex(interfaceIndex)}>{itemNo}:</a>
                    )
                  }
                  {it.debugMessage}
                </li>
              )
            })
          }
        </ul>
      ) : (
        <p>No issues.</p>
      )
    }
  </div>
)

export interface InterfaceProductWithMechOptions {
  interfaceProduct: InterfaceProduct
  mechOptions: MechProduct[]
  parentInterfaceProduct?: InterfaceProduct | null
}

export type TabButtons = { title: string | null, subtitle: string | null, items: readonly InterfaceProductWithMechOptions[] }

/**
 * returns a map of category names to interfaces/mechs
 * @param products
 * @param buttonOptions
 * @param selectedColor colour of the selected skin to configure
 * @param isPro if the selected skin to configure is Pro Series
 * @param showTopLevelMechOptions specifies whether interfaces should be duplicated to show different mech options as selectable items. This is not used in electrician "more info" / mech selection mode.
 */
function getInterfacesByCategory(
  products: ProductDataShape,
  buttonOptions: readonly InterfaceProduct[],
  selectedColor: IconicColor | null,
  isPro: boolean,
  showTopLevelMechOptions: boolean,
): Partial<Record<InterfaceProductCategory, TabButtons[]>> {
  const map: Partial<Record<InterfaceProductCategory, TabButtons[]>> = { }

  TabOptions.forEach(tabOption => {
    const mechsInCategory = products.mechs.filter(it => it.category === tabOption.key)
    const mechsInCategoryItemNos = mechsInCategory.map(it => it.itemNo)

    const unsortedButtonsInCategory = buttonOptions.reduce<InterfaceProductWithMechOptions[]>((accum, interfaceProduct) => {
      // Add interfaces with no mechs that match the category
      if (interfaceProduct.mechs.length === 0 && interfaceProduct.category === tabOption.key) {
        accum.push({
          interfaceProduct,
          mechOptions: [],
          parentInterfaceProduct: findParentInterface(interfaceProduct, products.interfaces)
        })

        return accum
      }

      const getMechsForCurrentCategoryAndSeries = (mechItemNos: readonly string[]) => {
        return mechItemNos
          .filter(mech => mechsInCategoryItemNos.includes(mech))
          .map(mechItemNo => {
            return findMechByItemNo(mechItemNo, products.mechs)
          })
          .filter(mech => {
            return mech.proCompatible === true
              || (mech.pro === true) === isPro
          })
      }

      const compatibleMechsForCategory = getMechsForCurrentCategoryAndSeries(interfaceProduct.mechs)

      let mechsToDisplayInCategory: MechProduct[]

      if (showTopLevelMechOptions) {
        const displayedMechsOverride = interfaceProduct.displayedMechs ? getMechsForCurrentCategoryAndSeries(interfaceProduct.displayedMechs) : null
        mechsToDisplayInCategory = displayedMechsOverride ?? compatibleMechsForCategory.slice(0, 1)
      } else {
        mechsToDisplayInCategory = compatibleMechsForCategory
      }

      if (mechsToDisplayInCategory.length > 0) {
        if (showTopLevelMechOptions) {
          accum.push(
            ...mechsToDisplayInCategory.map(mech => (
              {
                interfaceProduct,
                mechOptions: [mech],
                parentInterfaceProduct: findParentInterface(interfaceProduct, products.interfaces)
              }
            ))
          )
        } else {
          accum.push(
            {
              interfaceProduct,
              mechOptions: mechsToDisplayInCategory,
              parentInterfaceProduct: findParentInterface(interfaceProduct, products.interfaces)
            }
          )
        }

        return accum
      }

      return accum
    }, [])

    // stable sort interfaces to show those matching the skin colour first, other compatible colours are shown after
    const buttonsInCategory = selectedColor
      ? lodash.sortBy(unsortedButtonsInCategory, it => it.interfaceProduct.color === selectedColor ? 0 : 1)
      : unsortedButtonsInCategory

    const NO_TITLE = 'NO_TITLE'

    const keyGetter: (item: InterfaceProductWithMechOptions) => string
      = showTopLevelMechOptions
        // group by mech subcategories when showing mech options at top level
        ? (it => it.mechOptions[0]?.subcategory ?? it.interfaceProduct.subcategory ?? NO_TITLE)
        // group by interface subcategories when showing mech selection per interface
        : (it => it.interfaceProduct.subcategory ?? NO_TITLE)

    const groupMap = lodash.groupBy(buttonsInCategory, keyGetter)
    const groupList: {
      title: string
      subtitle: string | null
      items: readonly InterfaceProductWithMechOptions[]
      sortIndex: number
    }[] = lodash.keys(groupMap)
      .map(key => {
        // sort subcategories by the order of occurrence in the mechs data (as defined in the data spreadsheet)
        let occurrenceIndex = products.mechs.findIndex(mech => mech.subcategory === key)
        // sort to end if not found
        if (occurrenceIndex === -1) {
          occurrenceIndex = 100000
        }

        const [title, subtitle] = key.split('/')

        return {
          title,
          subtitle: subtitle ?? null,
          items: groupMap[key],
          sortIndex: key === NO_TITLE ? -2 : occurrenceIndex,
        }
      })

    // lodash sort is stable, we just want to move the untitled section to the front,
    // and otherwise maintain the default group order based on the order of products in the data
    map[tabOption.key] = lodash.sortBy(
      groupList,
      it => it.sortIndex
    )
      .map(it => it.title === NO_TITLE ? { ...it, title: null } : it)
  })

  return map
}

export interface ButtonConfigurationOptions {
  buttons: readonly ConfiguredInterface[]
  ledEnabled: boolean
}

const CustomiseMechsLabel = 'Customise Mechs'
const SwitchCustomisationFilterCategory = 'switch customisation'

const ButtonConfigurationScreen: React.FC<{
  isInEditMode?: boolean
  product: SkinProductOptionWithGrid
  onDone: (config: ButtonConfigurationOptions) => void
  onBack: (config: ButtonConfigurationOptions) => void
  initialLedEnabled: boolean
  initialButtonSelection: readonly ConfiguredInterface[]
  onChangeSkinOption: (gridItemNo: string) => void
  shouldShowPackQuantity: boolean
  addWiserHubToList: (ctaName: string) => void
}> = ({
  product,
  onDone,
  onBack,
  initialButtonSelection,
  initialLedEnabled,
  isInEditMode,
  shouldShowPackQuantity,
  addWiserHubToList
}) => {
  const analyticsLogger = React.useContext(AnalyticsContext)

  const isProSeries = product.pro === true

  const { wallColor, debugMode, dispatch, optionalWiserMode, shouldShowCustomiseHint, moreInfo, userType, hasOptedOutOfWiserHubPrompt } = React.useContext(ConfiguratorAppContext)

  const [debugShowAll, setDebugShowAll] = React.useState(false)

  const Products = React.useContext(ProductDataContext)

  const tabsForProductType = getAllowedInterfaceCategories(product.gridProduct.type, isProSeries)

  const buttons = Products.interfaces

  const buttonOptions = React.useMemo(() => {
    if (debugShowAll && debugMode) {
      return Products.interfaces.filter(it => !it.includedComponents || it.includedComponents.length === 0)
    }

   // If no color then no compatible interfaces.
    if (product.color === null) {
      return []
    }

    return getCompatibleInterfaces(product.color, isProSeries, Products.interfaces)
  }, [debugShowAll, debugMode, product.color, isProSeries, Products.interfaces])

  const [hintShown, setHintShown] = React.useState<'shown' | 'will-show' | 'hidden'>(shouldShowCustomiseHint ? 'will-show' : 'hidden')

  const [cancelPopupShown, setCancelPopupShown] = React.useState(false)

  const buttonOptionsContainerRef = React.createRef<HTMLDivElement>()

  React.useEffect(() => {
    dispatch({ action: AppStateActions.CustomiseHintShown })
  }, [dispatch])

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setHintShown(current => {
        return current === 'will-show' ? 'shown' : current
      })
    }, 500)

    return () => {
      clearTimeout(timer)
    }
  }, [])

  const clearHintText = () => setHintShown('hidden')

  const displayedButtonsMap = React.useMemo(() => {
    return getInterfacesByCategory(Products, buttonOptions, product.color, isProSeries, !moreInfo)
  }, [Products, buttonOptions, product.color, isProSeries, moreInfo])

  // show tabs that:
  // - are allowed for the product type
  // - have products, or is LED, which has no products
  const interfaceTabOptions = TabOptions.filter(it =>
    tabsForProductType.includes(it.key)
      && (
        (displayedButtonsMap[it.key]?.length ?? 0) > 0 || it.key === 'LED'
      )
  )

  const [selectedTab, setSelectedTab] = React.useState<InterfaceProductCategory>(interfaceTabOptions[0]?.key ?? 'Switches')

  const displayedButtons: TabButtons[] = displayedButtonsMap[selectedTab] ?? []

  const getExpandableContentForSection = (category: InterfaceProductCategory, subcategory: string | null): React.ReactNode => {
    if (category === 'Bluetooth' && subcategory === InterfaceProductSubcategory.Wiser) {

      return (
        <div className="explanationSection subcategory">
          {BuildConfig.brand.copy.WiserExtraInformation}
        </div>
      )
    }

    if (category === 'Bluetooth' && subcategory === InterfaceProductSubcategory.RemoteSwitches) {
      return (
        <div className="explanationSection subcategory">
          If you are switching these from a secondary location, you will need one of the below remote switches and corresponding switch plate at the secondary location.
        </div>
      )
    }

    if (category === 'Bluetooth' && subcategory === InterfaceProductSubcategory.CBus) {
      return (
        <div className="explanationSection subcategory">
          {BuildConfig.brand.copy.CbusExtraInformation}
        </div>
      )
    }

    return null
  }

  const getFixedContentForSection = (category: InterfaceProductCategory, subcategory: string | null): React.ReactNode => {
    if (category === 'Switches' && subcategory === InterfaceProductSubcategory.Pictograms && BuildConfig.brand.copy.PictogramsDescription) {
      return (
        <div className="explanationSection subcategory">
          {BuildConfig.brand.copy.PictogramsDescription}
        </div>
      )
    }

    return null
  }

  const getExtraContentForCategory = (category: InterfaceProductCategory): React.ReactNode => {
    if (category === 'LED' && !isProSeries) {
      return (
        <div className="explanationSection">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            Enable LED
            <ToggleSwitch
              checked={ledEnabled}
              onChange={() => {
                analyticsLogger.log({
                  event: 'cta_click',
                  cta_name: 'Enable LED',
                  cta_link: null,
                  cta_category: 'Toggle',
                  cta_location: 'Customise switches'
                })

                setLedEnabled(!ledEnabled)
              }}
            />
          </div>
          <p>
            Subtle warm white LEDs can be set as indicators, to show when switches or sockets are in use.<br/>Or as locators, so it&apos;s always easy to find them in the dark.
          </p>
          <p>
            If a product can be LED enabled, and you request it, the necessary components will be added to your final product list.
          </p>
        </div>
      )
    }

    return null
  }

  const getDefaultMechForWiser = (wiserMode: WiserMode) => {
    if (wiserMode.type === WiserOrMomentary.Wiser) {
      return Products.constants.WiserSwitchDefaultMech
    }

    return wiserMode.mechOverride
  }

  const getInitialButtons = (): ButtonIdStack => {
    if (optionalWiserMode !== null) {
      const wiserDefaultMech: string | null = getDefaultMechForWiser(optionalWiserMode)

      const defaultMechInterface = wiserDefaultMech !== null
        ? buttonOptions.find(it => it.mechs.includes(wiserDefaultMech)) ?? null
        : null

      if (defaultMechInterface !== null) {
        const configuredInterface: ConfiguredInterface = {
          interfaceItemNo: defaultMechInterface.itemNo,
          mechItemNo: wiserDefaultMech
        }

        const slotCount = getSkinSlotCount(product)
        return [new Array(slotCount).fill(configuredInterface)]
      }
    }

    return [resolveInterfaces(product, initialButtonSelection, Products, true)]
  }

  const [configuredButtonIdStack, setConfiguredButtonIds] = React.useState<ButtonIdStack>(getInitialButtons)
  const configuredButtonIds = configuredButtonIdStack[0]

  const configuredButtons: readonly Readonly<InflatedConfiguredInterfaceProduct>[] = configuredButtonIds.map((item) => {
    return inflateConfiguredInterface(item, Products)
  })

  const pushConfiguredButtons = React.useCallback(
    (func: ((current: readonly Readonly<ConfiguredInterface>[]) => ConfiguredInterface[])) => {
      setConfiguredButtonIds(state => {
        const prev = state[0]
        const newEntry = func(prev)

        // don't push if nothing has changed
        if (JSON.stringify(prev) === JSON.stringify(newEntry)) {
          return state
        }

        return [newEntry, ...state]
      })
    }, [setConfiguredButtonIds]
  )

  const popConfiguredButtons = React.useCallback(
    () => setConfiguredButtonIds(stack => stack.slice(Math.min(stack.length - 1, 1))), [setConfiguredButtonIds]
  )

  const [selectedButtonIndex, setSelectedButtonIndex] = React.useState(0)
  const [preventTooltips, setPreventTooltips] = React.useState(false)

  const configuredButtonRef = React.useRef<HTMLDivElement | null>(null)

  const getTabByInterfaceAndMech = (interfaceItemNo: string, mechItemNo: string | null) => {
    let tabName = null
    TabOptions.forEach(tabOption => {
      const matchingItemsInTab = displayedButtonsMap[tabOption.key]
        ?.map(set => set.items)
        .flat()
        .filter(item => item.interfaceProduct.itemNo === interfaceItemNo)
        .filter(item => {
          // Some interfaces are in more than 1 tab so we also need to match against mechs if it has one.
          if (mechItemNo === null) {
            return true
          }

          return item.mechOptions.map(option => option.itemNo).includes(mechItemNo)
        })

      if (matchingItemsInTab && matchingItemsInTab.length > 0) {
        tabName = tabOption.key
      }
    })

    return tabName
  }

  // When a slot is selected on the mockup, jump to the tab with the interface occupying that slot.
  React.useEffect(() => {
    if (selectedButtonIndex > -1) {
      const clickedInterfaceItemNo = configuredButtons.map(button => button.interfaceProduct.itemNo)[selectedButtonIndex]
      const mechItemNo = configuredButtons.map(button => button.mechProduct?.itemNo)[selectedButtonIndex] ?? null
      const tabWithInterface = getTabByInterfaceAndMech(clickedInterfaceItemNo, mechItemNo)
      if (tabWithInterface !== null && selectedTab !== tabWithInterface) {
        setSelectedTab(tabWithInterface)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedButtonIndex])

  React.useEffect(() => {
    const clickFrameListener = (e: Event) => {
      if (configuredButtonRef.current?.contains(e.target as HTMLDivElement)) {
        setSelectedButtonIndex(-1)
      }
    }

    // Unselect button if you click anywhere except a button (componentDidMount).
    document.addEventListener('click', clickFrameListener)
    return () => {
      document.removeEventListener('click', clickFrameListener)
    }
  },[])

  React.useEffect(() => {
    const mouseMoveListener = (e: Event) => {
      setPreventTooltips(false)
    }

    if (preventTooltips) {
      document.addEventListener('mousemove', mouseMoveListener)
      return () => {
        document.removeEventListener('mousemove', mouseMoveListener)
      }
    }
  },[preventTooltips])

  const onSelectButtonOption = React.useCallback(
    (buttonIndex: number, buttonOption: InterfaceProduct | null, mech: MechProduct | null) => {
      clearHintText()
      if (buttonIndex !== -1 && buttonOption !== null) {
        pushConfiguredButtons(currentButtons => {
          return currentButtons.map((button, index) => index === buttonIndex ? {
            interfaceItemNo: buttonOption.itemNo,
            mechItemNo: mech?.itemNo ?? null
          } : button)
        })

        return true
      } else {
        return false
      }
    }, [pushConfiguredButtons]
  )

  const onSwapButtonOptions = React.useCallback(
    (targetIndex: number, originIndex: number) => {
      clearHintText()
      setPreventTooltips(true)

      pushConfiguredButtons(currentButtons => {
        const droppedButton = currentButtons[originIndex]
        const draggedButton = currentButtons[targetIndex]

        const newButtons = currentButtons.slice()

        newButtons[originIndex] = draggedButton
        newButtons[targetIndex] = droppedButton

        return newButtons
      })

      return true
    }, [pushConfiguredButtons]
  )

  const hasMadeChanges = configuredButtonIdStack.length > 1

  const [ledEnabled, setLedEnabled] = React.useState(initialLedEnabled)
  // can appear for LED, which only shows LED toggle and info
  const isEmptySection = displayedButtons.length === 0
  const canShowLedPreview = selectedTab === 'LED' && isEmptySection
  // const isButtonsSection = selectedTab === 'Switches'

  const onClickDone = () => {
    if (!isInEditMode) {
      analyticsLogger.log({
        event: 'cta_click',
        cta_name: 'Add to list',
        cta_link: null,
        cta_category: 'Button',
        cta_location: 'Customise switches'
      })
    }

    const interfaceItemNos = configuredButtons
      .map(button => button.interfaceProduct.itemNo)
      .join(',')

    const mechItemNos = configuredButtons
      .map(button => button.mechProduct?.itemNo)
      .filter(it => typeof it === 'string')
      .join(',')

    analyticsLogger.log({
      event: 'customise_switches',
      interfaces: interfaceItemNos,
      mechanisms: mechItemNos.length > 0 ? mechItemNos : null,
    })

    if (
      !isInEditMode
      && !hasOptedOutOfWiserHubPrompt
      && configuredButtons.find(button => productRequiresWiserHub(button.mechProduct?.itemNo ?? '', Products.rules))
    ) {
      setShowingWiserPrompt(true)
    } else {
      onDone({ buttons: configuredButtonIds, ledEnabled })
    }
  }

  const onNavigateBack = () => onBack({ buttons: configuredButtonIds, ledEnabled })

  let onClickBack: () => void

  if (hasMadeChanges) {
    onClickBack = () => setCancelPopupShown(true)
  } else {
    onClickBack = onNavigateBack
  }

  const [showingWiserPrompt, setShowingWiserPrompt] = React.useState(false)

  const onWiserPromptAction = (params: { shouldAddWiserHub: boolean, doNotAskAgain: boolean }) => {
    setShowingWiserPrompt(false)

    if (params.doNotAskAgain) {
      dispatch({ action: AppStateActions.DisableWiserHubPrompt })
    }

    onDone({ buttons: configuredButtonIds, ledEnabled })
    if (params.shouldAddWiserHub) {
      addWiserHubToList('Done')
    }
  }

  const [size, setSize] = React.useState<readonly [number, number]>([100, 100])

  const productType = product.gridProduct.type

  const validationMessages = React.useMemo(() => validateMechs(Products, configuredButtons, productType, product.grid), [Products, configuredButtons, product.grid, productType])

  const isValid = validationMessages.length === 0

  const resetButtonIds = () => pushConfiguredButtons((_) => getDefaultButtons(product, Products))

  const doneText = isInEditMode ? (
    <>
      <img src={IconTick} alt="" style={{ marginRight: '10px' }}/>
      Done
    </>
  ) : (
    <>
      <img src={IconPlusWhite} alt="" style={{ marginRight: '10px' }}/>
      Add to list
    </>
  )

  const resetButton = (
    <Button
      mobileRound
      buttonStyle="grey"
      onClick={resetButtonIds}
    >
      <img src={IconReset} alt="" /><span className="hideMobile" style={{ marginLeft: '10px' }}>Reset</span>
    </Button>
  )

  const undoButton = (
    <Button
      mobileRound
      buttonStyle="grey"
      onClick={popConfiguredButtons}
      disabled={!hasMadeChanges}
    >
      <img src={IconUndo} alt="" /><span className="hideMobile" style={{ marginLeft: '10px' }}>Undo</span>
    </Button>
  )

  const [validationPopupShown, setValidationPopupShown] = React.useState(false)

  const remoteButtonControls: RemoteButtonControls = {
    selectedButtonIndex,
    onSelectButtonOption,
    configuredButtonIds,
    shouldShowPackQuantity,
  }

  return (
    <>
      <div
        className={
          classNames('ButtonConfigurationScreen', {
            ledEnabled: ledEnabled && canShowLedPreview,
            defaultWallColor: wallColor === WallColors[0].color,
          })
        }
        style={{
          backgroundColor: wallColor,
        }}
      >
        <div className="ButtonConfiguration-button-options-column" key="button options">
          <div className="ButtonConfiguration-button-options">
            <div className="buttonHeader">

              <Button buttonStyle="grey" onClick={onClickBack}>
                Back
              </Button>

              <div style={{ flex: '1 1 0' }} />

              {undoButton}
              <div style={{ flex: '0 1 10px' }} />
              {resetButton}
            </div>
            <TabSelector
              onClick={(tabKey) => {
                analyticsLogger.log({
                  event: 'filter_clicked',
                  filter_action: 'filter selected',
                  filter_category: SwitchCustomisationFilterCategory,
                  filter_sub_category: null,
                  filter_name: TabOptions.find(tab => tab.key === tabKey)?.label ?? tabKey,
                })

                // When navigating tabs always scroll to the top of tab content when it is selected.
                setSelectedTab(tabKey)
                if (buttonOptionsContainerRef.current) {
                  buttonOptionsContainerRef.current.scrollTop = 0
                }
              }}
              selected={selectedTab}
              options={interfaceTabOptions}
            />
            {
              debugMode && (
                <CheckBox checked={debugShowAll} setChecked={setDebugShowAll}>
                  Debug: Show all colours
                </CheckBox>
              )
            }
            <div className="buttonOptionContainer" ref={buttonOptionsContainerRef}>
              {
                userType === UserType.Electrician && ENABLE_ELECTRICIAN_VIEW && !isEmptySection && (
                  <div
                    className="explanationSection"
                    style={{ display: 'flex', justifyContent: 'flex-end' }}
                  >
                    <ToggleSwitch
                      checked={moreInfo}
                      label={CustomiseMechsLabel}
                      onChange={() => {
                        const willEnable = !moreInfo

                        analyticsLogger.log({
                          event: 'filter_clicked',
                          filter_action: willEnable ? 'filter selected' : 'filter deselected',
                          filter_category: SwitchCustomisationFilterCategory,
                          filter_sub_category: TabOptions.find(tab => tab.key === selectedTab)?.label ?? selectedTab,
                          filter_name: CustomiseMechsLabel,
                        })

                        analyticsLogger.log({
                          event: 'cta_click',
                          cta_name: 'Customise Mechs',
                          cta_link: null,
                          cta_category: 'Toggle',
                          cta_location: 'Customise switches'
                        })

                        dispatch({ action: AppStateActions.ToggleMoreInfo })
                      }}
                    />
                  </div>
                )
              }
              {getExtraContentForCategory(selectedTab)}
              {
                displayedButtons.map<JSX.Element>((subcategoryGroup, index, array) => {
                  const previousSection = array[index - 1] ?? null
                  const isFirstOfSection = subcategoryGroup.title !== null && subcategoryGroup.title !== previousSection?.title

                  const expandableContent = getExpandableContentForSection(selectedTab, subcategoryGroup.title)
                  const fixedContent = getFixedContentForSection(selectedTab, subcategoryGroup.title)

                  return (

                    <ButtonConfigurationGroup
                      key={`${subcategoryGroup.title || '_no title'}/${subcategoryGroup.subtitle ?? '_no subtitle'}`}
                      subcategoryGroup={subcategoryGroup}
                      expandableContent={expandableContent}
                      fixedContent={fixedContent}
                      isButtonsSection={false} // formerly displayed pictos as small size
                      remoteButtonControls={remoteButtonControls}
                      titleShown={isFirstOfSection}
                    />
                  )
                })
              }
            </div>
          </div>
          {
            debugMode && (
              <DebugValidationMessages
                validationMessages={validationMessages}
                onClickButtonIndex={setSelectedButtonIndex}
              />
            )
          }
        </div>
        <div className={'product-column'} key="product-column" ref={configuredButtonRef}>
          <div
            className="desktopButtonHeader"
            style={{ backgroundColor: 'transparent' }}
          >
            <Button buttonStyle="green" onClick={onClickDone} disabled={!isValid}>
              {doneText}
            </Button>
          </div>
          <div className="ButtonConfiguration-product">
            {
              !isValid && (
                <ValidationBanner
                  onDetails={() => setValidationPopupShown(true)}
                  top={'6px'}
                >
                  This combination isn&apos;t allowed
                </ValidationBanner>
              )
            }
            <ReactResizeDetector handleHeight handleWidth onResize={(x, y) => setSize([x, y])}/>
            <SquareContainer size="400px" maxSize={`${Math.min(...size)}px`} insetPercent={5}>
              <ConfigurableProductView
                className="ButtonConfigurationConfigurableProductView"
                {...product}
                shadowScale={Math.min(Math.max(0, Math.min(...size) / 300), 1)}
                buttonCoordsOrientation={getDefaultSkinOrientation(product)}
                image={product.image}
                placeholderImage={getSkinPlaceholderImage(product, product.gridProduct)}
                errorImage={getSkinErrorImage(product, product.gridProduct)}
                renderButton={(config: ButtonConfig, index: number, scale: number) => {
                  const buttonProduct = configuredButtons[index]
                  const { color } = buttonProduct.interfaceProduct
                  const { interfaceProduct, mechProduct } = buttonProduct

                  const { itemNo } = interfaceProduct
                  const mechItemNo = mechProduct?.itemNo ?? null

                  const useHintText = hintShown === 'shown' && index === selectedButtonIndex

                  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
                    const data = ButtonOptionIndexDragDrop.getData(e)
                    if (data !== null) {
                      const { interfaceItemNo, mechItemNo } = data
                      const item = mechItemNo === null
                        ? buttons.find(it => it.mechs.length === 0 && it.itemNo === interfaceItemNo)
                        : buttons.find(it => it.itemNo === interfaceItemNo && it.mechs.includes(mechItemNo))

                      if (item) {

                        if (ButtonOptionIndexDragDrop.getSwappedData(e) !== null) {
                          const swappedData = ButtonOptionIndexDragDrop.getSwappedData(e)

                          if (swappedData !== null) {

                            const { originIndex } = swappedData

                            setSelectedButtonIndex(index)
                            onSwapButtonOptions(index, originIndex)
                          }
                        } else {
                          onSelectButtonOption(index, buttons.find(it => it.itemNo === item.itemNo) ?? null, findMechByItemNo(mechItemNo, Products.mechs))
                          setSelectedButtonIndex(index)
                        }
                      } else {
                        console.warn('item not found', 'interface:', interfaceItemNo, 'mech:', mechItemNo)
                      }
                    }
                  }

                  const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
                    ButtonOptionIndexDragDrop.putData(e, itemNo, mechItemNo, index)
                    addZoomedDragElement(e, 1.2, (e.currentTarget as HTMLDivElement))
                  }

                  let tooltip: string | null = null

                  if (!preventTooltips) {
                    if (useHintText) {
                      if (windowSupportsTouchEvents) {
                        tooltip = 'Tap an item to add it here'
                      } else {
                        tooltip = 'Click or drag and drop an item to add it here'
                      }
                    } else {
                      tooltip = getInterfaceDisplayName(buttonProduct.interfaceProduct, buttonProduct.mechProduct)
                    }
                  } else {
                    tooltip = null
                  }

                  return (
                    <React.Fragment key={`${index} ${interfaceProduct.itemNo}`}>
                      {
                        !canShowLedPreview && (
                          <ConfigurableProductButton
                            backgroundImage={mechProduct?.image}
                            foregroundImage={interfaceProduct.image || PlaceholderInterface}
                            tooltip={tooltip}
                            alwaysShowTooltip={useHintText}
                            key={`${index} ${interfaceProduct.itemNo}`}
                            {...config}
                            baseScale={scale}
                            selected={selectedButtonIndex === index}
                            onClick={(e) => {
                              e.nativeEvent.stopImmediatePropagation()
                              setSelectedButtonIndex(currentIndex => {
                                if (currentIndex === index) {
                                  // Unselect the button if currently selected and hint shown.
                                  return hintShown === 'shown' ? index : -1
                                }

                                return index
                              })
                            }}
                            onDragStart={onDragStart}
                            onDrop={onDropHandler}
                            onDragOver={buttonOptionIndexDragOverHandler}
                            setSelected={setSelectedButtonIndex}
                            selectedButtonIndex={selectedButtonIndex}
                            debugColorCode={color}
                          />
                        )
                      }
                      {
                        canShowLedPreview && (
                          <ConfigurableProductButton
                            backgroundImage={mechProduct?.image}
                            foregroundImage={interfaceProduct.image || PlaceholderInterface}
                            key={`${index} ${interfaceProduct.itemNo}`}
                            {...config}
                            baseScale={scale}
                            debugColorCode={color}
                          />
                        )
                      }
                      {
                        canShowLedPreview && isLedSupported(interfaceProduct, mechProduct) && (
                          <ConfigurableProductButtonFrame
                            // tooltip={buttonProduct.label}
                            key={`highlight-${index}`}
                            {...config}
                            scale={scale}
                          >
                            <div className="ledHighlight"/>
                          </ConfigurableProductButtonFrame>
                        )
                      }

                      {
                        !!validationMessages.find(it => it.interfaceIndex === index) && (
                          <ConfigurableProductButtonFrame
                            key={`dot-${index}`}
                            {...config}
                            scale={scale}
                          >
                            <div className="dot warning"/>
                          </ConfigurableProductButtonFrame>
                        )
                      }
                    </React.Fragment>
                  )
                }}
              />
            </SquareContainer>
            {/*<ConfigurableProductCanvasView*/}
            {/*  {...product}*/}
            {/*  orientation={product.orientation}*/}
            {/*  buttonCoordsOrientation={getDefaultSkinOrientation(product)}*/}
            {/*  configuredButtons={configuredButtons}*/}
            {/*  size={300}*/}
            {/*/>*/}
          </div>
          <div className="cookieLinkSpacer" />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <PrivacyPolicyLink/>
            <CookieSettingsLink/>
          </div>
        </div>
        <div
          className="mobileButtonHeader"
          style={{ backgroundColor: 'transparent' }}
        >
          <Button buttonStyle="grey" onClick={onClickBack}>
            Back
          </Button>
          <div className="mobileButtons">
            {undoButton}
            <div style={{ flex: '0 1 10px' }} />
            {resetButton}
          </div>
          <span style={{ marginRight: '10px' }} />

          <Button buttonStyle="green" onClick={onClickDone} disabled={!isValid}>
            {doneText}
          </Button>
        </div>
      </div>
      {
        validationPopupShown && (
          <Popup onClose={() => setValidationPopupShown(false)}>
            <div className="title">
              Here&apos;s what&apos;s wrong:
            </div>
            <div className="validationMessages">
              {
                lodash.uniqBy(validationMessages, 'message')
                  .map((it, i) => {
                    const { message } = it
                    return (
                      <p key={i}>
                        {message}
                      </p>
                    )
                  })
              }
            </div>
            <div className="subtitle">
              You&apos;ll need to update your selection before you can add this to your list.
            </div>
            <div className="buttonRow">
              <Button buttonStyle="link" onClick={() => setValidationPopupShown(false)}>
                Close
              </Button>
            </div>
          </Popup>
        )
      }
      {
        showingWiserPrompt && (
          <WiserHubPromptPopup onAction={onWiserPromptAction} />
        )
      }
      {
        cancelPopupShown && (
          <CancelPopup
            onDismiss={() => setCancelPopupShown(false)}
            onConfirm={onNavigateBack}
          />
        )
      }
    </>
  )
}

export default ButtonConfigurationScreen
